/**
 * Stale-build detection: has a newer build been deployed than the one this tab is running?
 *
 * A single-page app keeps running the code it loaded. A tab opened before a deploy runs the OLD
 * build until someone reloads, however long it stays open. QA re-tested fixes on such tabs and
 * failed them: PGSS-5563's re-test reported a header that was, word for word, the pre-fix code.
 * Nobody could tell from the screen, so fixed tickets went back as Failed.
 *
 * The build publishes its stamp as /version.json (vite.config.mjs, versionFilePlugin) -- the same
 * values the bundle carries in versionInfo.js. The running app compares the two and offers a reload.
 *
 * The decision is kept apart from the component so it can be tested directly.
 */
import { APP_BUILT_AT, APP_COMMIT } from './versionInfo';

/** Often enough that a tester sees the prompt before re-testing; cheap, since the file is tiny. */
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** The stamp this tab is running. */
export const runningBuild = () => ({ commit: APP_COMMIT, built_at: APP_BUILT_AT });

/**
 * True only when BOTH stamps are present and they differ.
 *
 * Anything missing means "cannot tell", which must never prompt: a dev server has no version.json
 * (its catch-all serves index.html, which does not parse), and an unstamped build has nothing to
 * compare. A false prompt on every tab would teach people to ignore the real one.
 *
 * `built_at` is part of the identity, not just the commit: a rebuild of the same commit (a changed
 * environment variable, say) is still a different bundle.
 */
export function isNewerBuild(running, deployed) {
  if (!running?.built_at || !deployed?.built_at) {
    return false;
  }
  return running.built_at !== deployed.built_at || (running.commit || '') !== (deployed.commit || '');
}

/**
 * Read the deployed stamp. Resolves to null on ANY failure -- offline, 404, HTML instead of JSON --
 * because a failed check is "cannot tell", never "new build".
 *
 * `fetch`, not axios: axios is pointed at the API, and version.json lives beside index.html. The
 * query string and no-store get past the browser and any CDN cache, which would otherwise hand back
 * the stamp from the build this tab already has.
 */
export async function fetchDeployedBuild(fetchImpl = globalThis.fetch) {
  try {
    const res = await fetchImpl(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res?.ok) {
      return null;
    }
    const body = await res.json();
    return body && typeof body === 'object' ? body : null;
  } catch {
    return null;
  }
}
